// src/pages/placeholders/index.jsx

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import {
  Stethoscope, FileSearch, BookOpen, Activity,
  ClipboardList, Dumbbell, FolderOpen, FileSpreadsheet, FileText,
  MessageSquare, BarChart3, Calendar, CreditCard, Video, Settings
} from 'lucide-react';

// Base placeholder component
const PlaceholderPage = ({ title, description, icon: Icon, comingSoon = true }) => {
  return (
    <div className="space-y-6">
      <PageHeader title={title} />
      
      <div className="mx-auto max-w-3xl">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          {Icon && (
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-primary/10 mb-6">
              <Icon className="h-12 w-12 text-primary" />
            </div>
          )}
          
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            {comingSoon ? `${title} - Coming Soon` : title}
          </h2>
          
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            {description}
          </p>
          
          {comingSoon && (
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-yellow-50 text-yellow-800 text-sm font-medium">
              🚧 Under Development
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Clinical/EHR pages
export const EncountersPage = () => (
  <PlaceholderPage
    title="Encounters"
    description="Manage patient encounters, document visits, and track clinical interactions. This module will integrate with clinical notes and billing."
    icon={Stethoscope}
  />
);

export const ClinicalNotesPage = () => (
  <PlaceholderPage
    title="Clinical Notes"
    description="Create and manage SOAP notes, progress notes, and other clinical documentation. Features will include templates, voice dictation, and smart phrases."
    icon={FileSearch}
  />
);

// Exercise pages
export const ExerciseLibraryPage = () => (
  <PlaceholderPage
    title="Exercise Library"
    description="Browse and search our comprehensive exercise database. Filter by body part, equipment, difficulty, and clinical conditions."
    icon={BookOpen}
  />
);

export const CPGLibraryPage = () => (
  <PlaceholderPage
    title="Clinical Practice Guidelines"
    description="Access evidence-based clinical practice guidelines for rehabilitation. Includes condition-specific protocols and treatment algorithms."
    icon={Activity}
  />
);

export const RehabProgramsPage = () => (
  <PlaceholderPage
    title="Rehab Programs"
    description="Create and manage comprehensive rehabilitation programs. Build multi-phase programs with progression criteria and outcome tracking."
    icon={ClipboardList}
  />
);

export const WorkoutBuilderPage = () => (
  <PlaceholderPage
    title="Workout Builder"
    description="Design custom workout plans for your patients. Drag-and-drop interface with exercise variations and progression options."
    icon={Dumbbell}
  />
);

export const WorkoutPlansPage = () => (
  <PlaceholderPage
    title="Workout Plans"
    description="View and manage all workout plans. Track patient adherence, modify plans, and share with patients via mobile app."
    icon={FolderOpen}
  />
);

export const TemplatesPage = () => (
  <PlaceholderPage
    title="My Templates"
    description="Save and manage your exercise and program templates. Create templates for common conditions and share with your team."
    icon={FileSpreadsheet}
  />
);

// Form Builder pages
export const FormsPage = () => (
  <PlaceholderPage
    title="Form Builder"
    description="Create custom intake forms, assessments, and questionnaires. Drag-and-drop builder with conditional logic and scoring."
    icon={FileText}
  />
);

export const FormResponsesPage = () => (
  <PlaceholderPage
    title="Form Responses"
    description="View and manage patient form submissions. Export data, generate reports, and track completion rates."
    icon={MessageSquare}
  />
);

export const FormReportsPage = () => (
  <PlaceholderPage
    title="Form Reports"
    description="Analyze form data with customizable reports. Track outcomes, identify trends, and export for research."
    icon={BarChart3}
  />
);

// Other feature pages
export const SchedulePage = () => (
  <PlaceholderPage
    title="Schedule"
    description="Manage appointments, availability, and scheduling. Includes online booking, reminders, and calendar sync."
    icon={Calendar}
  />
);

export const MessagesPage = () => (
  <PlaceholderPage
    title="Secure Messages"
    description="HIPAA-compliant messaging with patients and team members. Includes attachments, templates, and automated responses."
    icon={MessageSquare}
  />
);

export const BillingPage = () => (
  <PlaceholderPage
    title="Billing"
    description="Process payments, manage invoices, and track revenue. Integrates with Stripe for payment processing and Claims.MD for insurance billing."
    icon={CreditCard}
  />
);

export const TelehealthPage = () => (
  <PlaceholderPage
    title="Telehealth"
    description="Conduct virtual visits with integrated video, screen sharing, and clinical documentation. Powered by Daily.co."
    icon={Video}
  />
);

// Admin pages
export const OrganizationPage = () => (
  <PlaceholderPage
    title="Organization Settings"
    description="Manage organization details, locations, and practice settings. Configure workflows and customize the platform for your practice."
    icon={Settings}
  />
);

export const FeaturesPage = () => (
  <PlaceholderPage
    title="Feature Management"
    description="Enable or disable platform features. Manage subscriptions and configure feature-specific settings."
    icon={Settings}
  />
);